import { ComparisonStatus } from './constants';

/**
 * Formats a number to exactly 2 decimal places.
 * Used for consistent display in comparison tables.
 */
export const formatMetricValue = (value: number): string => {
    return value.toFixed(2);
};

/**
 * Formats an "Actual | Expected" cell string with uniform width.
 * Each number: up to 5 digits before decimal, exactly 2 after.
 * Uses padStart for alignment so the '|' separator stays vertically aligned.
 */
export const formatComparisonCell = (actual: number, expected: number): string => {
    const maxWidth = 8; // 5 digits + '.' + 2 decimals = 8 chars
    const actualStr = actual.toFixed(2).padStart(maxWidth);
    const expectedStr = expected.toFixed(2).padStart(maxWidth);
    return `${actualStr} | ${expectedStr}`;
};

/**
 * Maps ComparisonStatus to display color.
 * Colors align with Meridian semantic tokens:
 * - CRITICAL → error/red
 * - AT_RISK → warning/orange-amber
 * - ON_TRACK → informational/blue
 * - AHEAD → success/green
 */
export const STATUS_COLORS: Record<string, string> = {
    [ComparisonStatus.CRITICAL]: '#d13212',
    [ComparisonStatus.AT_RISK]: '#eb5f07',
    [ComparisonStatus.ON_TRACK]: '#0073bb',
    [ComparisonStatus.AHEAD]: '#1d8102',
};

/**
 * Returns the color for a given ComparisonStatus.
 * Falls back to default text color if status is unknown.
 */
export const getStatusColor = (status: string | undefined): string => {
    if (!status) return 'inherit';
    return STATUS_COLORS[status] || 'inherit';
};

/**
 * Maps a ComparisonStatus to the Meridian Tag `type` used for the delta badge:
 * - CRITICAL → error (red)
 * - AT_RISK  → warning (amber)
 * - AHEAD / ON_TRACK → success (green)
 * Unknown/undefined statuses fall back to a neutral tag.
 */
export const getStatusTagType = (
    status: string | undefined
): 'success' | 'warning' | 'error' | 'neutral' => {
    switch (status) {
        case ComparisonStatus.CRITICAL:
            return 'error';
        case ComparisonStatus.AT_RISK:
            return 'warning';
        case ComparisonStatus.AHEAD:
        case ComparisonStatus.ON_TRACK:
            return 'success';
        default:
            return 'neutral';
    }
};

/**
 * Formats a variance percentage into a signed, whole-number badge label
 * (e.g. 2.4 → "+2%", -6.1 → "-6%"). Matches the delta pills in the design.
 */
export const formatDeltaPercent = (variancePercentage: number | undefined): string => {
    if (variancePercentage == null || Number.isNaN(variancePercentage)) return '';
    const rounded = Math.round(variancePercentage);
    return `${rounded > 0 ? '+' : ''}${rounded}%`;
};
